// ─── React & Libraries ───────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

// ─── UI Components ──────────────────────────────────────────
import TextInput from "../../hook_forms/hook_textInput";
import PrimaryButton from "../../common/PrimaryButton";
import Spinner from "../../common/Spinner";
import { ToastContainer, toast } from "react-toastify";

// ─── API Services ───────────────────────────────────────────
import {
  addNewOrganization,
  addNewOrganizationUser,
  editOrganization,
} from "../../../setup/api/services/organization";

// ─── Styles ─────────────────────────────────────────────────
import styles from "./AddOrganisation.module.scss";

// ─────────────────── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";
import { uploadFile } from "../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../s3Integration/enums";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import PhoneInput from "../../hook_forms/hook_phoneInput/PhoneInput";

// ─── Props ──────────────────────────────────────────────────
interface Props {
  onNext: (password?: string) => void;
}

// ─── StepTwo Component ──────────────────────────────────────
// Handles admin information and submits the organization form.
const StepTwo = ({ onNext }: Props) => {
  const { t } = useTranslation(); // ✅ For translations

  // React Hook Form methods
  const {
    register,
    watch,
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [loading, setLoading] = useState(false);

  // ─── Zustand Selectors ─────────────────────────────────────
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );
  const setSelectedOrganization = useOrganisationStore(
    (state) => state.setSelectedOrganization
  );

  // Watch values
  const adminName = watch("adminName");
  const email = watch("email");
  const mobile = watch("mobile");

  const userId = AuthConfig.getUserInfo().id;

  const uploadImage = useCallback(
    (logo: File): Promise<string> => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          if (logo) {
            const logoUrl = await uploadFile(
              logo,
              EUploadImageType.PROFILE,
              userId
            );
            resolve(logoUrl);
          } else {
            reject(new Error("Logo file is null"));
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [userId]
  );

  // ─── Submit Handler ───────────────────────────────────────
  const handleSubmitStepTwo = useCallback(async () => {
    const formValues = getValues();
    const {
      name,
      code,
      fees,
      websiteUrl,
      logo,
      description,
      adminName,
      email,
      mobile,
    } = formValues;

    setLoading(true);
    let logoUrl = "";

    if (typeof logo !== "string") {
      if (logo && logo.length > 0 && logo[0]) {
        try {
          logoUrl = await uploadImage(logo[0]);
        } catch (error) {
          logoUrl = "";
        }
      } else {
        logoUrl = "";
      }
    } else {
      logoUrl = logo;
    }
    const payload = {
      name,
      code,
      fees,
      logoUrl: logoUrl || "",
      websiteUrl,
      description,
    };

    try {
      if (selectedOrganisation?.code) {
        await editOrganization(selectedOrganisation.code, payload);
        onNext();
      } else {
        await addNewOrganization(payload);
        const parts = adminName.trim().split(" ");
        const body = {
          email,
          firstName: parts[0],
          lastName: parts.slice(1).join(" "),
          roles: ["CLIENT_ADMIN"],
          organizationCode: code,
          phoneNumber: `+966${mobile}`,
        };
        const response = await addNewOrganizationUser(body);
        onNext(response?.data?.clientUserToken);
      }
      toast(t("organization_saved_successfully"));
    } catch (error) {
      console.error(error);
      toast(t("organization_save_error"));
    } finally {
      setLoading(false);
      setSelectedOrganization();
    }
  }, [
    getValues,
    onNext,
    selectedOrganisation?.code,
    setSelectedOrganization,
    t,
    uploadImage,
  ]);

  useEffect(() => {
    if (selectedOrganisation?.code) {
      handleSubmitStepTwo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation]);

  // ─── Validation Check ─────────────────────────────────────
  const isNextDisabled = useMemo(() => {
    return (
      !adminName ||
      adminName.length < 2 ||
      !email ||
      !email.includes("@") ||
      !mobile
    );
  }, [adminName, email, mobile]);

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className={styles.step_container}>
      {/* Admin Name */}
      {!selectedOrganisation?.code && (
        <>
          <div className={styles.inputs}>
            <TextInput
              {...register("adminName")}
              placeholder={t("administrator_name")}
              errorMessage={errors.adminName?.message?.toString()}
            />
          </div>

          {/* Email */}
          <div className={styles.inputs}>
            <TextInput
              {...register("email")}
              placeholder={t("email")}
              errorMessage={errors.email?.message?.toString()}
            />
          </div>

          {/* Mobile Number */}
          <div className={styles.inputs}>
            <Controller
              name="mobile"
              control={control}
              defaultValue=""
              render={({ field, fieldState }) => (
                <PhoneInput
                  {...field}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </div>
        </>
      )}

      {/* Submit */}
      <div className={styles.button_container}>
        <PrimaryButton
          color="primary"
          onClick={handleSubmitStepTwo}
          disabled={loading || isNextDisabled}>
          {loading ? <Spinner /> : t("next")}
        </PrimaryButton>
      </div>

      <ToastContainer />
    </div>
  );
};

export default StepTwo;
